import { Lightbulb, Sprout, Star, Target } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/core/card';
import { TextArea } from '../../components/core/textarea';
import { StatRing } from './stat-ring';
import { HomeworkWeeks } from './homework-weeks';
import { PresenceDots, HomeworkDots, type FeedbackSession, type SessionHref } from './session-dots';
import { EditableListCard } from './editable-list-card';
import { AbilityCheckCard } from './ability-check-card';
import { TagCard } from './tag-card';
import type { AbilityScore, FeedbackContent } from './types';

const SECTION_CARD_CLASS = 'rounded-xl border border-rose-200 border-b-[4px] border-b-rose-300 bg-gradient-to-b from-white to-gray-50/60 shadow-[inset_0_1px_0_rgba(255,255,255,0.8),0_4px_0_rgba(0,0,0,0.05),0_6px_12px_rgba(0,0,0,0.04)]';

interface FeedbackViewProps {
  content: FeedbackContent;
  editable?: boolean;
  onContentChange?: (content: FeedbackContent) => void;
  /** Student's CEFR level (e.g. "B1"), shown next to the Ability Check title. */
  levelName?: string | null;
  /**
   * The classes of the report's period, computed now. When given, attendance
   * and homework are drawn as one dot per class (instead of the stored ring /
   * weeks) and each dot can open its class through `classHref`.
   */
  sessions?: FeedbackSession[];
  classHref?: SessionHref;
}

export function FeedbackView({ content, editable = false, onContentChange, levelName, sessions, classHref }: FeedbackViewProps) {
  const { t } = useTranslation('admin');
  const hasSessions = Boolean(sessions && sessions.length > 0);

  const updateField = <K extends keyof FeedbackContent>(key: K, value: FeedbackContent[K]) => {
    onContentChange?.({ ...content, [key]: value });
  };

  const updateListItem = (field: 'strengths' | 'attentionAreas' | 'recommendations', index: number, value: string) => {
    const list = [...content[field]];
    list[index] = value;
    updateField(field, list);
  };

  const addListItem = (field: 'strengths' | 'attentionAreas' | 'recommendations') => {
    updateField(field, [...content[field], '']);
  };

  const deleteListItem = (field: 'strengths' | 'attentionAreas' | 'recommendations', index: number) => {
    updateField(field, content[field].filter((_, idx) => idx !== index));
  };

  const updateAbilityScore = (index: number, score: AbilityScore) => {
    if (!content.abilityChecks) return;
    const next = content.abilityChecks.map((ability, idx) =>
      idx === index ? { ...ability, score } : ability,
    );
    updateField('abilityChecks', next);
  };

  return (
    <div className="space-y-8">
      {/* Engagement Stats */}
      {content.engagementStats && (
        <div className="space-y-4">
          <h2 className="text-sm font-semibold uppercase tracking-[0.3em] text-brand">
            {t('studentFeedback.sectionEngagement')}
          </h2>
          <Card className={SECTION_CARD_CLASS}>
            <CardContent className="flex flex-col items-center gap-3 py-6">
              <div className="flex flex-wrap items-start justify-center gap-6">
                {hasSessions ? (
                  <PresenceDots sessions={sessions!} classHref={classHref} />
                ) : (
                  <StatRing
                    id="presence"
                    label={t('studentFeedback.statPresence')}
                    percentage={content.engagementStats.presence.percentage}
                    delay={0}
                  />
                )}
                {hasSessions ? (
                  <HomeworkDots sessions={sessions!} classHref={classHref} />
                ) : content.engagementStats.submissions.weeks?.length ? (
                  <HomeworkWeeks weeks={content.engagementStats.submissions.weeks} />
                ) : (
                  <StatRing
                    id="submissions"
                    label={t('studentFeedback.statSubmissions')}
                    percentage={content.engagementStats.submissions.percentage}
                    delay={200}
                  />
                )}
                <StatRing
                  id="extra-class"
                  label={t('studentFeedback.statExtraClassPresence')}
                  percentage={content.engagementStats.extraClassPresence.percentage}
                  delay={400}
                />
              </div>
              {hasSessions && classHref && (
                <p className="text-xs text-gray-500">{t('studentFeedback.dotsHint')}</p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Feedback cards */}
      <div className="space-y-4">
        <h2 className="text-sm font-semibold uppercase tracking-[0.3em] text-brand">
          {t('studentFeedback.sectionFeedback')}
        </h2>
        <div className="grid gap-4 md:grid-cols-3">
          <EditableListCard
            title={t('studentFeedback.strengths')}
            items={content.strengths}
            icon={Star}
            onItemChange={(i, v) => updateListItem('strengths', i, v)}
            onItemAdd={() => addListItem('strengths')}
            onItemDelete={(i) => deleteListItem('strengths', i)}
            editable={editable}
          />
          <EditableListCard
            title={t('studentFeedback.attentionAreas')}
            items={content.attentionAreas}
            icon={Target}
            onItemChange={(i, v) => updateListItem('attentionAreas', i, v)}
            onItemAdd={() => addListItem('attentionAreas')}
            onItemDelete={(i) => deleteListItem('attentionAreas', i)}
            editable={editable}
          />
          <EditableListCard
            title={t('studentFeedback.recommendations')}
            items={content.recommendations}
            icon={Lightbulb}
            onItemChange={(i, v) => updateListItem('recommendations', i, v)}
            onItemAdd={() => addListItem('recommendations')}
            onItemDelete={(i) => deleteListItem('recommendations', i)}
            editable={editable}
          />
        </div>
      </div>

      {/* Growth Narrative */}
      <Card className="rounded-xl border border-rose-200 border-b-[4px] border-b-rose-300 bg-gradient-to-b from-white to-rose-50/40 shadow-[inset_0_1px_0_rgba(255,255,255,0.8),0_4px_0_rgba(0,0,0,0.05),0_6px_12px_rgba(0,0,0,0.04)]">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base font-semibold text-[#cf2e2e]">
            <Sprout className="h-4 w-4 text-rose-400" aria-hidden="true" />
            {t('studentFeedback.growthNarrative')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {editable ? (
            <TextArea
              value={content.growthNarrative}
              onChange={(e) => updateField('growthNarrative', e.target.value)}
              rows={4}
              className="rounded-lg border-gray-200 text-gray-700"
            />
          ) : (
            <p className="text-sm leading-relaxed text-gray-700">{content.growthNarrative}</p>
          )}
        </CardContent>
      </Card>

      {/* Skills & Themes */}
      <div className="space-y-4">
        <h2 className="text-sm font-semibold uppercase tracking-[0.3em] text-[#cf2e2e]">
          {t('studentFeedback.sectionExplored')}
        </h2>
        <div className="grid gap-4 md:grid-cols-2">
          <TagCard tone="skill" title={t('studentFeedback.skillsLearned')} items={content.skillsLearned} />
          <TagCard tone="theme" title={t('studentFeedback.classThemes')} items={content.classThemes} />
        </div>
      </div>

      {/* Ability Check (final feedback only) */}
      {content.abilityChecks && content.abilityChecks.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-sm font-semibold uppercase tracking-[0.3em] text-[#cf2e2e]">
            {levelName ? `${t('studentFeedback.sectionAbilityCheck')} - ${levelName}` : t('studentFeedback.sectionAbilityCheck')}
          </h2>
          <AbilityCheckCard
            abilityChecks={content.abilityChecks}
            editable={editable}
            onChange={updateAbilityScore}
          />
        </div>
      )}
    </div>
  );
}
